// src/pages/ContactPage.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "../services/contactMessages.api";
import { apiRequest } from "../services/api";
import { LanguageContext } from "../context/LanguageContext";

function ContactPage() {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const whatsappMessage =
  lang === "EN" ? "I need help" : "أحتاج مساعدة";

const whatsappLink = `https://wa.me/96179338100?text=${encodeURIComponent(
  whatsappMessage
)}`;

  const TEXT = {
    EN: {
      title: "Contact Us",
      chat: "Messages / Chat",
      fullName: "Full name *",
      phone: "Phone *",
      email: "Email (optional)",
      subject: "Subject",
      message: "Your message *",
      send: "Send Message",
      sending: "Sending…",
      success: "Message sent successfully 🤍",
      back: "Go back",
    },
    AR: {
      title: "تواصلي معنا",
      chat: "الرسائل / المحادثة",
      fullName: "الاسم الكامل *",
      phone: "رقم الهاتف *",
      email: "البريد الإلكتروني (اختياري)",
      subject: "الموضوع",
      message: "رسالتك *",
      send: "إرسال الرسالة",
      sending: "جاري الإرسال…",
      success: "تم إرسال الرسالة بنجاح 🤍",
      back: "العودة",
    },
  }[lang];

  /* 🔔 unread admin replies */
 useEffect(() => {
  async function loadUnread() {
    // 🚫 guest users have no unread messages
    if (!localStorage.getItem("esill-token")) {
      setUnreadCount(0);
      return;
    }

    try {
      const res = await apiRequest("/contact-messages/unread-count");
      setUnreadCount(res.unread || 0);
    } catch {
      setUnreadCount(0);
    }
  }

  loadUnread();
}, []);


    /* 👤 Prefill form if user is logged in */
  useEffect(() => {
    try {
      const cachedUser = localStorage.getItem("esill-user");
      if (!cachedUser) return;

      const user = JSON.parse(cachedUser);

      setForm((prev) => {
  const rawPhone = user.phone || "";
  const rawEmail = user.email || "";

  const phoneLooksLikeEmail = rawPhone.includes("@");

  return {
    ...prev,
    full_name: prev.full_name || user.full_name || "",
    phone: prev.phone || (phoneLooksLikeEmail ? "" : rawPhone),
    email: prev.email || rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
  };
});
    } catch {
      // silent fail
    }
  }, []);


  const submit = async () => {
  if (!form.full_name || !form.phone || !form.message) return;

  setSending(true);

  const res = await sendMessage(form); // ⬅️ backend returns { id }

  setSending(false);

  // 👉 go directly to chat with this thread
  navigate("/chat", {
    state: { messageId: res.id },
  });
};


  if (sent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <p className="text-lg font-semibold mb-4">{TEXT.success}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 bg-black text-white rounded-md text-sm font-semibold"
        >
          {TEXT.back}
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-full min-h-screen bg-white pt-[95px] px-4 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* ❌ BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[110px] text-3xl opacity-60 hover:opacity-100"
        style={lang === "AR" ? { right: "16px" } : { left: "16px" }}
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center tracking-wide text-[#2E3135]">
        {TEXT.title}
      </h1>
      <div className="h-[2px] w-16 bg-black mx-auto mt-2 mb-8" />

      {/* CHAT CTA */}
      <div className="max-w-md mx-auto mb-6 flex justify-center">
        <button
          onClick={() => {
    if (!localStorage.getItem("esill-token")) {
      localStorage.setItem("esill-open-login", "1");
      navigate("/");
      return;
    }
    navigate("/chat");
  }}
            className="
            relative flex items-center gap-2
            px-5 py-2.5 border border-black
            rounded-full text-sm font-semibold
            hover:bg-black hover:text-white transition
          "
        >
          💬 {TEXT.chat}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[20px] h-[20px] rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center px-1">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* FORM */}
      <div className="max-w-md mx-auto space-y-4">
        <Input placeholder={TEXT.fullName} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <Input placeholder={TEXT.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Input placeholder={TEXT.email} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <Input placeholder={TEXT.subject} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />

        <textarea
          rows={4}
          placeholder={TEXT.message}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full border border-[#E6D8D2] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black"
        />

        <button
          onClick={submit}
          disabled={sending}
          className="w-full py-3 bg-black text-white rounded-md font-semibold text-sm active:scale-[0.98]"
        >
          {sending ? TEXT.sending : TEXT.send}
        </button>
        <button
  onClick={() => window.open(whatsappLink, "_blank")}
  className="
    w-full py-3 mt-3
    border border-[#25D366]
    text-[#25D366]
    rounded-md
    font-semibold text-sm
    flex items-center justify-center gap-2
    hover:bg-[#25D366] hover:text-white
    transition
  "
>
  <img
    src="/icons/whatsapp.png"
    alt="WhatsApp"
    className="w-5 h-5"
  />
  {lang === "EN" ? "I prefer WhatsApp" : "أفضل واتساب"}
</button>

      </div>
    </div>
  );
}

function Input({ placeholder, value, onChange }) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#E6D8D2] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-black"
    />
  );
}

export default ContactPage;
